import { useEffect, useRef } from 'react';

const CELL    = 40;   // matches login grid backgroundSize
const LENGTH  = 1;    // solo la cabeza, sin cola
const STEP_MS = 260;  // ms to travel one full cell

type Pt = { x: number; y: number };

function lerp(a: number, b: number, t: number) { return a + (b - a) * t; }

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

// color = hex del acento (lo pasa el login según la marca activa). Default emerald.
export function GridSnake({ isDark, color = '#059669' }: { isDark: boolean; color?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;

    let w = 0, h = 0, cols = 0, rows = 0;

    const resize = () => {
      w    = canvas.width  = canvas.offsetWidth;
      h    = canvas.height = canvas.offsetHeight;
      cols = Math.floor(w / CELL);
      rows = Math.floor(h / CELL);
    };
    resize();

    const cx = Math.max(2, Math.floor(cols / 2));
    const cy = Math.max(2, Math.floor(rows / 2));

    // prevBody → currBody are the two grid states we interpolate between
    let currBody: Pt[] = Array.from({ length: LENGTH }, () => ({ x: cx, y: cy }));
    let prevBody: Pt[] = currBody.map(p => ({ ...p }));
    let dx = 1, dy = 0;
    let lastStep = 0; // initialised on first frame

    const DIRS = [
      { dx: 1, dy: 0 }, { dx: -1, dy: 0 },
      { dx: 0, dy: 1 }, { dx: 0, dy: -1 },
    ];

    const doStep = () => {
      const head = currBody[0];
      const options = DIRS.filter(d => {
        if (d.dx === -dx && d.dy === -dy) return false;
        const nx = head.x + d.dx;
        const ny = head.y + d.dy;
        return nx >= 1 && nx <= cols - 2 && ny >= 1 && ny <= rows - 2;
      });
      if (!options.length) return;

      const straight = options.find(d => d.dx === dx && d.dy === dy);
      const chosen = straight && Math.random() < 0.90
        ? straight
        : options[Math.floor(Math.random() * options.length)];

      dx = chosen.dx;
      dy = chosen.dy;

      prevBody = currBody.map(p => ({ ...p }));
      currBody = [{ x: head.x + dx, y: head.y + dy }, ...currBody.slice(0, LENGTH - 1)];
    };

    // Color del acento de la marca activa (emerald por defecto).
    const { r: R, g: G, b: B } = hexToRgb(color);

    const draw = (ts: number) => {
      if (lastStep === 0) lastStep = ts;

      // advance steps with += so t is always computed AFTER the timer is updated,
      // preventing the "snap back" that happens when elapsed > STEP_MS
      while (ts - lastStep >= STEP_MS) {
        doStep();
        lastStep += STEP_MS;
      }

      // t ∈ [0, 1) — linear, constant velocity between intersections
      const t = (ts - lastStep) / STEP_MS;

      ctx.clearRect(0, 0, w, h);

      // glowing head dot
      const hx = lerp(prevBody[0].x, currBody[0].x, t) * CELL;
      const hy = lerp(prevBody[0].y, currBody[0].y, t) * CELL;

      ctx.beginPath();
      ctx.arc(hx, hy, 3, 0, Math.PI * 2);
      ctx.fillStyle   = `rgba(${R},${G},${B},0.95)`;
      ctx.shadowBlur  = 18;
      ctx.shadowColor = `rgba(${R},${G},${B},0.8)`;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(hx, hy, 5.5, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(${R},${G},${B},0.22)`;
      ctx.lineWidth   = 1;
      ctx.shadowBlur  = 10;
      ctx.stroke();

      ctx.shadowBlur = 0;
      raf = requestAnimationFrame(draw);
    };

    let raf = requestAnimationFrame(draw);

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [isDark, color]);

  return (
    <canvas
      ref={ref}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
}
